import { TourAnchor } from "training/anchors";
import {
  all,
  collectsPayment,
  continueBlockedByAbn,
  directPayEnabled,
  marksPaid,
  not,
  reachesCheckout,
  showsContinue,
  showsDiscount,
  showsDiscountExpiry,
  showsPlanSwitch,
  usesFinix,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import { ESTIMATE_ACTIONS_MENU } from "./shared";

/** A reload on the patient page restarts at the Collect Payment click: that page cannot be opened by URL. */
const PATIENT_PAGE_RESUME = "m4.01-collect-payment";

/** 4.10, Direct Pay: the module's last step (review 1), on the hospital view. */
export const MODULE_4_DIRECT_PAY: ITourStep = {
  id: "m4.10-direct-pay",
  // First on the hospital view after a navigation.
  waitMs: 8000,
  module: 4,
  anchor: TourAnchor.DirectPay,
  label: "Direct Pay",
  mode: "look",
  route: "estimates",
  when: directPayEnabled,
  text: 'For money not tied to an estimate, such as an old balance or a walk-in. Name, date of birth, amount, and your account number, then "Continue to credit card". It makes its own estimate so it shows on your report. Look, do not fill it in.',
};

/**
 * CLE-3015 Module 4: How to collect payment. Text is John's (review 1, 2026-10-23) where he gave wording; the
 * rest is the curriculum's (curriculum-v3, 2026-10-21). Nothing here charges: Pay Now and the Mark paid
 * buttons are `look` steps, and the trainee leaves checkout with "Back to details".
 *
 * Review 1: an intro card opens the module and carries the two payment notes that were its last cards (the
 * Tuesday report, paying in the evening; 4.13 and 4.14 are gone); Mark paid via cash and via check are one
 * highlight; the receipt card ends the walk on the estimate, and Direct Pay (4.10), which had run in Module 6,
 * is the module's last step again, on the hospital view.
 *
 * Since the Estimate Details redesign (CLE-2961) "Accept payment" is "Collect Payment", a header button
 * since CLE-3018. Rows the curriculum gives one highlight and the page shows as several elements are split
 * (4.2, 4.6, 4.8).
 *
 * The $0 branch: a No Balance Due estimate has no Collect Payment, so 4.1–4.9 drop out (`collectsPayment`)
 * and the tour carries on at 4.11 / the cards.
 */
export const MODULE_4: ITourStep[] = [
  {
    // Review 1. The module's purpose (the popover's intro) carries John's first sentence.
    id: "m4.00-intro-card",
    module: 4,
    mode: "card",
    route: "estimate",
    text: [
      "A couple of notes about payment before we dive in:",
      "• Every payment that goes through Clear Health lands on your Tuesday report under the Account / Visit ID you typed. That is how your business office posts it.",
      "• Patients can pay from the text at any hour. Many pay in the evening after you have gone home for the day, removing the burden from your plate entirely!",
    ].join("\n"),
  },
  {
    id: "m4.01-collect-payment",
    module: 4,
    anchor: TourAnchor.CollectPayment,
    label: "Collect Payment",
    mode: "click",
    // The item logs in as the patient and opens /order/:externalId itself.
    advanceOn: "route",
    route: "estimate",
    when: collectsPayment,
    text: [
      "Let's say a patient has arrived to your desk and wants to pay for their estimate in person. This module will walk you through how to do this.",
      'First, you will search their name on your home page to find their Estimate. By clicking "Collect Payment" it will take you right to the check out page. This button also appears within each Estimate Details page that we reviewed in the last module. Both ways are acceptable, but accessing from the home screen will be faster.',
      'Click "Collect Payment" now.',
    ].join("\n"),
  },
  {
    id: "m4.02-billing-statement",
    // After a page change (the patient page; checkout loads its card form).
    waitMs: 8000,
    module: 4,
    anchor: TourAnchor.BillingStatement,
    label: "ACCOUNT SUMMARY",
    // The highlight is the whole statement; ACCOUNT SUMMARY is only one heading on it (manual pass 2026-10-26).
    title: "The patient's billing statement",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: collectsPayment,
    text: "This is the same page the patient saw in their text.",
  },
  {
    // Only on Patient Estimates and on Patient Balances with an insurance allowable (Home.tsx).
    id: "m4.02b-why-owe",
    module: 4,
    anchor: TourAnchor.WhyOwe,
    side: "top",
    label: "Why do I owe this?",
    mode: "fill",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: collectsPayment,
    optional: true,
    waitMs: 1500,
    text: "This drop down will detail the math behind why the patient owes this amount: their deductible, coinsurance, and copay against the contracted rate. It answers most questions before they ask. Click it to open it.",
  },
  {
    // The highlight takes in "Discount expires on" under the price (review 1; Home.tsx).
    id: "m4.03-pay-now-price",
    module: 4,
    anchor: TourAnchor.PayNowPrice,
    label: "Pay Now Price",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsDiscountExpiry),
    text: 'Paying before the visit earns the prompt-pay discount your facility set. The crossed-out figure is the price without it, and "Discount expires on" says how long it lasts.',
  },
  {
    // The 4.3 branch "no expiry date": the curriculum's text without the expiry clause.
    id: "m4.03n-pay-now-price-no-expiry",
    module: 4,
    anchor: TourAnchor.PayNowPrice,
    label: "Pay Now Price",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsDiscount, not(showsDiscountExpiry)),
    text: "Paying before the visit earns the prompt-pay discount your facility set. The crossed-out figure is the price without it.",
  },
  {
    // 4.4 is three clicks: "Payment Plan" shows the plan button, and "Pay in Full" puts the switch back —
    // left on "Payment Plan", Continue would go to the plan checkout, or stay greyed out with no plan.
    // The last sentence is not curriculum text.
    id: "m4.04-payment-plan-option",
    module: 4,
    anchor: TourAnchor.PaymentPlanOption,
    label: "Payment Plan",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    text: 'If the patient cannot pay it all, you can set up or change a plan right here at the desk. Click "Payment Plan".',
  },
  {
    // "Edit Payment Plan", or "Create Payment Plan" when the estimate has no plan.
    id: "m4.04b-payment-plan-button",
    module: 4,
    anchor: TourAnchor.PaymentPlanButton,
    side: "top",
    label: "Payment Plan",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    // Absent when the account offers no plan option the editor can create (PaymentPlan.tsx editorOptions).
    optional: true,
    waitMs: 3000,
    text: [
      "The default payment plan option will appear here. If payment plans are disabled, patients and staff will not be able to select this option. The Edit Payment Plan button will allow you to edit the terms of the plan.",
      "Important note! Consult with your manager on your flexibility in editing these plans so you know ahead of time!",
    ].join("\n"),
  },
  {
    // Not curriculum text.
    id: "m4.04c-pay-in-full-option",
    module: 4,
    anchor: TourAnchor.PayInFullOption,
    label: "Pay in Full",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    text: 'Click "Pay in Full" again to go on.',
  },
  {
    id: "m4.05-continue",
    module: 4,
    anchor: TourAnchor.Continue,
    side: "top",
    label: "Continue",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "This goes to checkout. Click it, and stop on the next screen.",
  },
  {
    // Not curriculum text — the 4.5 branch "Continue hidden": explain why and skip checkout.
    id: "m4.05x-no-continue",
    module: 4,
    mode: "card",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, not(showsContinue)),
    text: 'Your hospital has turned off "Continue" on this page, or hides it until the visit has a date, so there is no checkout to show here. Patients still pay from the link in their text.',
  },
  {
    // Not curriculum text — the 4.5 branch "disabled while a required ABN is unsigned".
    id: "m4.05y-abn-unsigned",
    module: 4,
    mode: "card",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsContinue, continueBlockedByAbn),
    text: 'This estimate needs an ABN (Advance Beneficiary Notice) signed before payment, so "Continue" stays greyed out until the patient signs it on this page. The tour skips checkout.',
  },
  {
    // First on checkout: it waits for the card form to load after Continue. (Before review 1 the optional
    // fees box below came first and carried that wait, so a Finix hospital without a credit-card fee sat
    // 8 s on a box that never came — John's "it took about 5-8 seconds to load".)
    id: "m4.06b-card-form",
    waitMs: 8000,
    module: 4,
    anchor: TourAnchor.CardForm,
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Type the card here, or hand the keyboard to the patient. Do not enter a card now.",
  },
  {
    // The surcharge sentence sits on the "Payment method fees" box it describes, which only Finix hospitals
    // with a credit-card fee show (the 4.6 branch). The card form is loaded by now: a short wait.
    id: "m4.06-payment-method-fees",
    waitMs: 1500,
    module: 4,
    anchor: TourAnchor.PaymentMethodFees,
    label: "Payment method fees",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout, usesFinix),
    optional: true,
    text: "A credit card carries a surcharge where your hospital has one; debit and HSA or FSA cards do not, and the box says so.",
  },
  {
    id: "m4.07-split-payment",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.SplitPayment,
    label: "Split payment between two cards",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "For a patient paying part on an HSA card and the rest on a credit card. Enter the first amount and the second fills itself.",
  },
  {
    id: "m4.08-terms",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.TermsCheckbox,
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "The patient must agree to the terms.",
  },
  {
    id: "m4.08b-pay-now",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.PayNow,
    label: "Pay Now",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Pay Now charges the card and sends a receipt to their phone or email. Then the estimate shows Paid, and they arrive prepaid with nothing to discuss.",
  },
  {
    id: "m4.09-back-to-details",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.BackToDetails,
    label: "Back to details",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Leave without paying.",
  },
  {
    // One highlight on both "Mark paid via cash" and "Mark paid via check" (review 1): the anchor is on the
    // group the "..." menu wraps them in (OrderHeader.tsx), so it shows whichever of the two the hospital
    // has. The id predates the group; m4.11b (check only) is gone.
    id: "m4.11-mark-paid-cash",
    module: 4,
    anchor: TourAnchor.MarkPaid,
    title: "Mark paid via cash or check",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: marksPaid,
    text: "If a patient paid at the desk via Check or Cash, the estimate can be updated accordingly. By marking these options, it will update the estimate to Paid and the texts stop. Follow your normal cash posting process for cash or check.",
  },
  {
    // The curriculum's "Receipt" is the menu item "Download Receipt" since the redesign.
    id: "m4.12-receipt-card",
    module: 4,
    mode: "card",
    route: "estimate",
    text: 'Once payment has been collected, some additional options will appear in the "...". A fully paid estimate will gain a "Download Receipt" option and an estimate partly paid gains "Pay Additional Amount" and "Update Card".',
  },
  MODULE_4_DIRECT_PAY,
];
